import React, { useState } from 'react';
import { RATING_EMOJIS } from '../../utils/constants';

function ReviewCard({ review, onPhotoClick, onSave, onUnsave, isSaved = false, onViewCreator, onUpgrade }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showDishDetails, setShowDishDetails] = useState(false);

  const currentDish = review.dishes?.find(d => d.photo_index === currentPhotoIndex);
  const isFreeTier = review.subscription_tier === 'free';
  const isMustGo = review.rating === 6;
  const isMustGoLocked = isFreeTier && isMustGo;
  const restaurant = review.restaurant || { name: review.restaurant_name, location: '', cuisine_types: [] };

  const handleSaveClick = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 600);
    if (isSaved) {
      onUnsave();
    } else {
      onSave();
    }
  };

  const handlePrevPhoto = (e) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : review.photos.length - 1));
    setShowDishDetails(false);
  };

  const handleNextPhoto = (e) => {
    e.stopPropagation();
    setCurrentPhotoIndex((prev) => (prev < review.photos.length - 1 ? prev + 1 : 0));
    setShowDishDetails(false);
  };

  const handlePhotoClick = () => {
    // Toggle dish details directly in the card
    if (currentDish) {
      setShowDishDetails(!showDishDetails);
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    // Open modal
    onPhotoClick(currentPhotoIndex);
  };

  return (
    <div className="bg-white rounded-card shadow-card overflow-hidden review-card" data-testid={`review-card-${review.review_id}`}>
      {/* Creator Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => onViewCreator && onViewCreator(review.creator.user_id)}>
          {review.creator?.picture ? (
            <img 
              src={review.creator.picture} 
              alt={review.creator.name}
              className="w-10 h-10 rounded-full object-cover hover:ring-2 hover:ring-primary transition-all"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm hover:ring-2 hover:ring-primary transition-all">
              {review.creator?.name?.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-semibold text-sm hover:text-primary transition-colors">{review.creator?.name}</p>
            <p className="text-xs text-gray-500">{new Date(review.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        <button
          onClick={handleSaveClick}
          className={`text-2xl transition-transform ${isAnimating ? 'animate-heart-bounce' : ''}`}
          data-testid={`save-button-${review.review_id}`}
        >
          {isSaved ? '❤️' : '🤍'}
        </button>
      </div>

      {/* Photo Carousel */}
      {review.photos && review.photos.length > 0 && (
        <div 
          className="w-full h-80 bg-gray-200 cursor-pointer overflow-hidden relative"
          onClick={handlePhotoClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          data-testid={`review-photo-${review.review_id}`}
        >
          <img
            src={review.photos[currentPhotoIndex]}
            alt={review.restaurant_name}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              showDishDetails && currentDish ? 'opacity-90' : 'opacity-100'
            }`}
          />
          
          {/* Dish Details Overlay - Shows when clicked */}
          {showDishDetails && currentDish && (
            <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none animate-fade-in" data-testid="card-dish-details">
              <div className="text-center max-w-md bg-black/50 p-6 rounded-xl backdrop-blur-md">
                <h4 className="text-white font-bold text-2xl mb-3 drop-shadow-lg">{currentDish.dish_name}</h4>
                <p className="text-white text-base leading-relaxed drop-shadow-lg">{currentDish.notes}</p>
              </div>
            </div>
          )}
          
          {/* Navigation Arrows - Show on hover if multiple photos */}
          {review.photos.length > 1 && isHovered && !showDishDetails && (
            <>
              <button
                onClick={handlePrevPhoto}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/90 transition-all z-10"
                data-testid={`card-prev-button-${review.review_id}`}
              >
                ‹
              </button>
              <button
                onClick={handleNextPhoto}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/70 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/90 transition-all z-10"
                data-testid={`card-next-button-${review.review_id}`}
              >
                ›
              </button>
            </>
          )}
          
          {/* Expand Button - Show on hover (bottom right) */}
          {isHovered && !showDishDetails && (
            <button
              onClick={handleExpandClick}
              className="absolute bottom-2 right-2 bg-black/70 text-white w-10 h-10 rounded-lg flex items-center justify-center hover:bg-black/90 transition-all z-10"
              data-testid={`card-expand-button-${review.review_id}`}
              title="Expand image"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}
          
          {/* Photo Counter - Show on hover if multiple photos */}
          {review.photos.length > 1 && isHovered && !showDishDetails && (
            <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold z-10">
              {currentPhotoIndex + 1} / {review.photos.length}
            </div>
          )}
          
          {/* Click hint - Show when hovering and has dish info */}
          {currentDish && isHovered && !showDishDetails && (
            <div className="absolute bottom-14 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-xs">
              Click to see dish details
            </div>
          )}
          
          {/* Dot Indicators */}
          {review.photos.length > 1 && !showDishDetails && (
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 z-10">
              {review.photos.map((_, index) => (
                <div
                  key={index}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    index === currentPhotoIndex ? 'bg-white w-4' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Must Go Lock (for free users) */}
      {isMustGoLocked && (
        <div className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border-t-4 border-yellow-400">
          <div className="text-center">
            <div className="mb-3">
              <span className="text-5xl">🤩</span>
              <span className="text-3xl ml-2">🔒</span>
            </div>
            <h4 className="font-heading font-bold text-lg mb-2">Must-Go Restaurant Locked</h4>
            <p className="text-sm text-gray-700 mb-4">
              This is one of {review.creator.name}'s absolute favorite spots! Unlock their hand-picked must-visit restaurants with premium.
            </p>
            <button
              onClick={() => onUpgrade && onUpgrade(review.creator.user_id, review.creator.name)}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 rounded-button font-semibold hover:shadow-lg transition-all"
            >
              Unlock Must-Go Spots ($7/mo)
            </button>
          </div>
        </div>
      )}

      {/* Content (only show if not Must Go locked) */}
      {!isMustGoLocked && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-heading font-bold text-xl">{restaurant.name}</h3>
              {restaurant.location && (
                <p className="text-xs text-gray-600 mt-0.5">📍 {restaurant.location}</p>
              )}
              {restaurant.cuisine_types && restaurant.cuisine_types.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {restaurant.cuisine_types.map((cuisine, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                      {cuisine}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end ml-4">
              <span className="text-3xl" data-testid={`rating-emoji-${review.review_id}`}>
                {RATING_EMOJIS[review.rating]?.emoji}
              </span>
              <span className="text-xs text-gray-600 mt-1">
                {RATING_EMOJIS[review.rating]?.label}
              </span>
            </div>
          </div>

          <p className={`text-textdark text-sm leading-relaxed mt-3 ${isFreeTier ? 'line-clamp-3' : ''}`}>{review.description}</p>
          
          {/* Insider Tips (Premium Only) */}
          {!isFreeTier && review.insider_tips && (
            <div className="mt-4 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
              <p className="text-xs font-semibold text-amber-800 mb-1">💡 Insider Tips</p>
              <p className="text-xs text-gray-700">{review.insider_tips}</p>
            </div>
          )}

          {/* Free Tier Upgrade CTA */}
          {isFreeTier && !isMustGo && (
            <div className="mt-3 bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/30 rounded-button p-3">
              <p className="text-xs font-semibold text-primary mb-1">🔓 Unlock Full Review</p>
              <p className="text-xs text-gray-700 mb-2">Get all photos, dish details, and insider tips with premium access</p>
              <button
                onClick={() => onUpgrade && onUpgrade(review.creator.user_id, review.creator.name)}
                className="w-full bg-primary text-white py-2 px-3 rounded-button text-xs font-semibold hover:shadow-hover transition-all"
                data-testid={`upgrade-cta-${review.review_id}`}
              >
                Upgrade to Premium ($7/mo)
              </button>
            </div>
          )}

          {/* Blurred Photos Preview (Free Tier) */}
          {isFreeTier && review.photos && review.photos.length > 1 && (
            <div className="mt-4 relative">
              <p className="text-xs text-gray-600 mb-2">{review.photos.length - 1} more photo{review.photos.length > 2 ? 's' : ''} available with premium</p>
              <div className="flex gap-2 overflow-hidden">
                <div className="w-20 h-20 bg-gray-200 rounded blur-sm flex items-center justify-center">
                  <span className="text-2xl">🔒</span>
                </div>
                <div className="w-20 h-20 bg-gray-200 rounded blur-sm flex items-center justify-center">
                  <span className="text-2xl">🔒</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReviewCard;
